(() => {
  if (customElements.get('gca-predictive-search')) return;

  class GcaPredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector(this.dataset.inputSelector || 'input[name="q"]');
      this.results = this.querySelector(this.dataset.resultsSelector);
      this.form = this.querySelector('form');
      if (!this.input || !this.results) return;
      this.timer = null;
      this.controller = null;
      this.input.addEventListener('input', () => this.schedule());
      this.input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          this.close();
          this.input.blur();
        }
      });
      document.addEventListener('pointerdown', (event) => {
        if (!this.contains(event.target)) this.close();
      });
    }

    schedule() {
      this.cancelPending();
      const term = this.input.value.trim();
      if (!term) return this.close();
      this.timer = setTimeout(() => this.search(term), 260);
    }

    async search(term) {
      this.cancelPending();
      const controller = new AbortController();
      this.controller = controller;
      const params = new URLSearchParams({
        q: term,
        'resources[type]': 'product',
        'resources[limit]': '6',
        'resources[options][unavailable_products]': 'hide',
        'resources[options][fields]': 'title,product_type,variants.title,variants.sku,vendor',
        section_id: 'predictive-search'
      });
      this.setAttribute('loading', '');
      try {
        const response = await fetch(this.dataset.routes + '?' + params.toString(), {
          signal: controller.signal,
          headers: { Accept: 'text/html' }
        });
        if (!response.ok) throw new Error('Predictive search failed: ' + response.status);
        const html = await response.text();
        if (this.controller !== controller || term !== this.input.value.trim()) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const section = doc.querySelector('#shopify-section-predictive-search');
        this.results.innerHTML = section ? section.innerHTML : '';
        this.rank(term);
        this.open();
      } catch (error) {
        if (this.controller === controller && error.name !== 'AbortError') this.close();
      } finally {
        if (this.controller === controller) {
          this.controller = null;
          this.removeAttribute('loading');
        }
      }
    }

    normalize(value) {
      return String(value || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, ' ').trim();
    }

    rank(term) {
      const list = this.results.querySelector('.gca-predictive-products');
      if (!list) return;
      const query = this.normalize(term);
      const compactQuery = query.replace(/\s+/g, '');
      const rows = [...list.querySelectorAll('.gca-predictive-product')];
      rows.forEach((row, index) => {
        const title = this.normalize(row.dataset.title);
        const handle = this.normalize(row.dataset.handle);
        const skus = (row.dataset.skus || '').split('|').map((sku) => this.normalize(sku)).filter(Boolean);
        const exact = title === query || handle.replace(/\s+/g, '') === compactQuery || skus.includes(query);
        const starts = title.startsWith(query) || skus.some((sku) => sku.startsWith(query));
        const contains = title.includes(query) || skus.some((sku) => sku.includes(query));
        row.dataset.rank = exact ? '0' : starts ? '1' : contains ? '2' : '3';
        row.dataset.originalIndex = String(index);
        row.classList.toggle('is-exact', exact);
      });
      rows.sort((a, b) => Number(a.dataset.rank) - Number(b.dataset.rank) || Number(a.dataset.originalIndex) - Number(b.dataset.originalIndex));
      rows.forEach((row) => list.appendChild(row));
    }

    open() {
      if (!this.results.innerHTML.trim()) return this.close();
      this.results.classList.add('is-open');
      this.input.setAttribute('aria-expanded', 'true');
    }

    cancelPending() {
      clearTimeout(this.timer);
      this.timer = null;
      if (this.controller) this.controller.abort();
      this.controller = null;
      this.removeAttribute('loading');
    }

    close() {
      this.cancelPending();
      this.results.classList.remove('is-open');
      this.input?.setAttribute('aria-expanded', 'false');
    }
  }

  customElements.define('gca-predictive-search', GcaPredictiveSearch);
})();
